import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import React, { useState } from 'react';

import { EmailNotificationSubscriptionDto } from '../../../types';
import { noop } from '../../../util/appUtil';
import {
  GROUP_STATUS_LABELS,
  subscriptionSignature,
  NOTIFICATION_TYPE_OPTIONS,
  sortedSubscriptions,
  subscriptionTypeLabel,
} from '../../../util/emailNotificationUtil';
import StatusToggle from './StatusToggle';
import styles from './emailNotifications.module.css';

/**
 * The stored subscription rows for one address, grouped by region.
 *
 * Both places that list these rows use this: the admin table's expanded detail, and the
 * subscriptions card on a user's profile. They were separate tables that had already
 * drifted -- different column order, one with a switch and one with an Activate button,
 * and the card naming no plant at all -- so the shape lives here once.
 *
 * Region is the grouping rather than a column because that is the unit people act on:
 * a region's plants are turned on or off together far more often than one at a time.
 * Each region heading carries a switch that does exactly that, which is several records
 * in one gesture -- hence `onToggle` taking a list rather than a row, so a caller
 * cannot accidentally fire one request per plant and report on them separately.
 *
 * Within a region, rows keep `sortedSubscriptions` order (topic, then plant), so the
 * same address reads the same way in both places and across reloads. Regions are
 * name-ordered.
 *
 * Status is settable because that is where it lives in the API and it is the only field
 * that is not part of a row's identity. Removing a subscription is not offered from
 * either caller: it goes through the add/edit modal, by unticking a plant.
 *
 * `groupByTopic` puts the topics on tabs, so the profile card reads the way the add/edit
 * modal does -- topic, then regions, then plants. The admin table's expansion leaves it
 * off: it is already nested inside a table row, where a tab bar reads as chrome on
 * chrome, and it lists one address rather than being the place that address is worked
 * on. Everything below the tabs is the same either way, which is the point of it being
 * a flag rather than a second component.
 */
/**
 * A stable identity for a row.
 *
 * `id` and `url` are both optional on the DTO, so a row could fall back to no key at
 * all and be reconciled by position. The signature is what the backend's uniqueness is
 * keyed on -- email, type, region, plant -- so it identifies the same subscription that
 * an id would, without needing one.
 */
const rowKey = (subscription: EmailNotificationSubscriptionDto) =>
  subscription.id ?? subscription.url ?? subscriptionSignature(subscription);

export default function SubscriptionRowsTable(props: SubscriptionRowsTableProps) {
  const {
    subscriptions,
    regionNames,
    plantLabel,
    canEdit = false,
    onToggle = noop,
    groupByTopic = false,
    dataTest = 'subscription-row',
  } = props;

  const [topicIndex, setTopicIndex] = useState(0);

  /**
   * Only the topics this address actually receives get a tab. The modal offers every
   * topic because it is choosing; this is reading out what was chosen, and a tab onto
   * an empty list is a dead end.
   */
  const topics = groupByTopic
    ? NOTIFICATION_TYPE_OPTIONS.filter(option =>
        subscriptions.some(subscription => subscription.notificationType === option.key),
      )
    : [];
  const topic = topics[Math.min(topicIndex, Math.max(topics.length - 1, 0))];

  const shown = topic
    ? subscriptions.filter(subscription => subscription.notificationType === topic.key)
    : subscriptions;

  /**
   * Regions in name order, each holding its rows in reading order.
   *
   * Built off `sortedSubscriptions` so the within-region order is the shared one; the
   * grouping only re-buckets what it returns, since that sorts topic-first and a
   * region-grouped table needs region-first.
   */
  const ordered = sortedSubscriptions(shown, regionNames, plantLabel);
  const byRegion = new Map<string, EmailNotificationSubscriptionDto[]>();
  ordered.forEach(subscription => {
    const url = subscription.region?.url ?? '';
    if (!byRegion.has(url)) {
      byRegion.set(url, []);
    }
    byRegion.get(url)!.push(subscription);
  });

  const regions = Array.from(byRegion.entries())
    .map(([url, rows]) => ({ url, name: regionNames[url] ?? url, rows }))
    .sort((a, b) => a.name.localeCompare(b.name));

  /**
   * A region's rollup. Three states rather than two: a region with three of five plants
   * sending is neither on nor off, and a switch claiming either misreports it. The
   * switch itself sits off unless every row is active, so clicking it turns the region
   * fully on -- which is what "off, partly" invites.
   */
  const rollupOf = (rows: EmailNotificationSubscriptionDto[]) => {
    const active = rows.filter(row => row.active).length;
    if (active === rows.length) {
      return { allActive: true, label: GROUP_STATUS_LABELS.active };
    }
    return {
      allActive: false,
      label: active > 0 ? GROUP_STATUS_LABELS.partial : GROUP_STATUS_LABELS.inactive,
    };
  };

  const renderRegion = (region: {
    url: string;
    name: string;
    rows: EmailNotificationSubscriptionDto[];
  }) => {
    const { allActive, label } = rollupOf(region.rows);

    /*
     * One row for the whole region: its name, the plants it covers as a list, and its
     * switch. The plants are named rather than counted so the switch is not asking to be
     * trusted blind, and they carry no control of their own -- the region is the unit.
     * With a switch on every plant and another on the region it was never obvious which
     * one a click was about to move.
     */
    if (groupByTopic) {
      return [
        <TableRow key={`region-${region.url}`} className={styles.subRegionRow}>
          <TableCell className={styles.subRegionCell} data-test={`${dataTest}-region`}>
            {region.name}
          </TableCell>
          <TableCell className={styles.subRegionPlants}>
            <span className={styles.subPlantList} data-test={`${dataTest}-plants`}>
              {region.rows.map(subscription => (
                <span
                  key={rowKey(subscription)}
                  className={styles.subPlantName}
                  data-test={`${dataTest}-plant`}
                >
                  {plantLabel(subscription)}
                </span>
              ))}
            </span>
          </TableCell>
          <TableCell data-test={`${dataTest}-region-status`}>
            <StatusToggle
              active={allActive}
              statusLabel={label}
              canEdit={canEdit}
              onToggle={active => onToggle(region.rows, active)}
              ariaLabel={`${allActive ? 'Deactivate' : 'Activate'} every notification in ${
                region.name
              }`}
              dataTest={`${dataTest}-region-toggle`}
            />
          </TableCell>
        </TableRow>,
      ];
    }

    return [
      <TableRow key={`region-${region.url}`} className={styles.subRegionRow}>
        <TableCell className={styles.subRegionCell} colSpan={2} data-test={`${dataTest}-region`}>
          {region.name}
        </TableCell>
        <TableCell data-test={`${dataTest}-region-status`}>
          <StatusToggle
            active={allActive}
            statusLabel={label}
            canEdit={canEdit}
            onToggle={active => onToggle(region.rows, active)}
            ariaLabel={`${allActive ? 'Deactivate' : 'Activate'} every notification in ${
              region.name
            }`}
            dataTest={`${dataTest}-region-toggle`}
          />
        </TableCell>
      </TableRow>,
      ...region.rows.map(subscription => (
        <TableRow key={rowKey(subscription)} hover>
          <TableCell data-test={`${dataTest}-type`}>
            {subscriptionTypeLabel(subscription)}
          </TableCell>
          <TableCell data-test={`${dataTest}-plant`}>{plantLabel(subscription)}</TableCell>
          <TableCell data-test={`${dataTest}-status`}>
            <StatusToggle
              active={!!subscription.active}
              canEdit={canEdit}
              onToggle={active => onToggle([subscription], active)}
              ariaLabel={`${
                subscription.active ? 'Deactivate' : 'Activate'
              } ${subscriptionTypeLabel(subscription)} for ${plantLabel(subscription)} on ${
                subscription.email
              }`}
              dataTest={`${dataTest}-toggle`}
            />
          </TableCell>
        </TableRow>
      )),
    ];
  };

  const table = (
    <Table className={styles.subTable} size="small">
      <TableHead>
        <TableRow>
          {groupByTopic ? (
            <>
              <TableCell>Region</TableCell>
              <TableCell>Plants</TableCell>
              <TableCell>Status</TableCell>
            </>
          ) : (
            <>
              <TableCell>Notification</TableCell>
              <TableCell>Plant</TableCell>
              <TableCell>Status</TableCell>
            </>
          )}
        </TableRow>
      </TableHead>
      <TableBody>{regions.flatMap(renderRegion)}</TableBody>
    </Table>
  );

  if (!groupByTopic || topics.length === 0) {
    return table;
  }

  return (
    <div>
      <Tabs
        value={Math.min(topicIndex, topics.length - 1)}
        onChange={(_event, next: number) => setTopicIndex(next)}
        /*
         * Not scrollable: at most one tab per notification topic, so there is nothing to
         * scroll. See the note in NotificationsField.
         */
        className={styles.subTabs}
      >
        {topics.map(option => (
          <Tab key={option.key} label={option.label} data-test={`${dataTest}-topic-tab`} />
        ))}
      </Tabs>
      {table}
    </div>
  );
}

export interface SubscriptionRowsTableProps {
  subscriptions: EmailNotificationSubscriptionDto[];
  /** Region url to display name. */
  regionNames: { [url: string]: string };
  /** Names a row's plant; build with `makePlantLabel`. */
  plantLabel: (subscription: EmailNotificationSubscriptionDto) => string;
  canEdit?: boolean;
  /**
   * Sets the status of one row or of a whole region, so it takes a list either way.
   * A region toggle is several records in one gesture, and a caller handed rows one at
   * a time would fire a request each and report on them separately.
   */
  onToggle?: (subscriptions: EmailNotificationSubscriptionDto[], active: boolean) => void;
  /**
   * Puts the topics on tabs, so the list reads the way the add/edit modal does. The
   * Notification column goes with it, since the tab carries that.
   */
  groupByTopic?: boolean;
  dataTest?: string;
}
